/**
 * API client with authentication
 * 
 * Wraps fetch calls to automatically include auth token and handle errors.
 * Supports mock mode for frontend-only development.
 */

import { useAuthToken, useCurrentUser, getAuthTokenOrThrow, SessionExpiredError } from '../auth';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { config, useMockApi } from '../config';
import { getMockResponse, hasMockData } from './mockData';
import { MockApiError } from '../mandate/mockApi';

const API_BASE_URL = config.apiUrl;

export interface ApiError {
  status: number;
  message: string;
  details?: unknown;
}

export class ApiClientError extends Error {
  public status: number;
  public details?: unknown;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.status = error.status;
    this.details = error.details;
  }
}

/**
 * Hook that provides an authenticated API client
 * 
 * Uses useRef to stabilize ALL dynamic values, ensuring the returned
 * api object never changes reference. This prevents infinite re-render
 * loops in consuming hooks that depend on the api object.
 */
export const useApiClient = () => {
  const { getToken, isLoaded, isSignedIn } = useAuthToken();
  const { tenantId } = useCurrentUser();
  const mockMode = useMockApi();
  
  // Stabilize all dynamic values into refs so the returned `request` callback
  // (with empty deps) reads the latest values without changing identity.
  const getTokenRef = useRef(getToken);
  const tenantIdRef = useRef(tenantId);
  const isLoadedRef = useRef(isLoaded);
  const isSignedInRef = useRef(isSignedIn);
  const mockModeRef = useRef(mockMode);
  useEffect(() => {
    getTokenRef.current = getToken;
    tenantIdRef.current = tenantId;
    isLoadedRef.current = isLoaded;
    isSignedInRef.current = isSignedIn;
    mockModeRef.current = mockMode;
  }, [getToken, tenantId, isLoaded, isSignedIn, mockMode]);

  const request = useCallback(async <T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> => {
    const method = options.method || 'GET';
    
    // In mock mode, return mock data for supported endpoints
    if (mockModeRef.current && hasMockData(endpoint)) {
      // Simulate network delay for realism
      await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
      // Stateful mock endpoints (mandates, library, proposals) need the request
      // body and can answer with an HTTP error, e.g. a 422 validation failure.
      let mockBody: unknown;
      if (typeof options.body === 'string') {
        try {
          mockBody = JSON.parse(options.body);
        } catch {
          mockBody = undefined;
        }
      }
      try {
        return getMockResponse(endpoint, method, mockBody) as T;
      } catch (err) {
        if (err instanceof MockApiError) {
          throw new ApiClientError({ status: err.status, message: err.message, details: err.details });
        }
        throw err;
      }
    }

    // Get auth token using stable ref. Retries transient failures internally;
    // throws SessionExpiredError (caught below) instead of silently sending
    // this request with no Authorization header when the user is expected
    // to be signed in but no token could be obtained.
    let token: string | null;
    try {
      token = await getAuthTokenOrThrow(getTokenRef.current, isSignedInRef.current);
    } catch (err) {
      if (err instanceof SessionExpiredError) {
        throw new ApiClientError({ status: 401, message: err.message, details: { code: 'SESSION_EXPIRED' } });
      }
      throw err;
    }

    // Build headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    // Add tenant ID header for multi-tenancy using stable ref
    if (tenantIdRef.current) {
      (headers as Record<string, string>)['X-Tenant-ID'] = tenantIdRef.current;
    }

    // Make request
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle errors
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      let details: unknown;

      try {
        const errorBody = await response.json();
        errorMessage = errorBody.error || errorBody.message || errorMessage;
        details = errorBody;
      } catch {
        // Body not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }

      throw new ApiClientError({
        status: response.status,
        message: errorMessage,
        details,
      });
    }

    // Parse response
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }, []); // No dependencies - all values accessed via stable refs

  // Create a completely stable api object using useMemo with empty deps
  // All dynamic values are accessed via refs at call time
  const api = useMemo(() => ({
    get: <T>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),
    
    post: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
    
    put: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),
    
    patch: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),
    
    delete: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }),
    
    // Upload file with multipart form data
    upload: async <T>(endpoint: string, formData: FormData): Promise<T> => {
      let token: string | null;
      try {
        token = await getAuthTokenOrThrow(getTokenRef.current, isSignedInRef.current);
      } catch (err) {
        if (err instanceof SessionExpiredError) {
          throw new ApiClientError({ status: 401, message: err.message, details: { code: 'SESSION_EXPIRED' } });
        }
        throw err;
      }

      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (tenantIdRef.current) {
        headers['X-Tenant-ID'] = tenantIdRef.current;
      }
      // Note: Don't set Content-Type for FormData, browser will set it with boundary

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorBody = await response.json();
          errorMessage = errorBody.error || errorBody.message || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new ApiClientError({ status: response.status, message: errorMessage });
      }

      return response.json();
    },

    // isReady is accessed via getter to always return current value
    get isReady() {
      return isLoadedRef.current && isSignedInRef.current;
    },
  }), [request]); // Only depends on request which is now stable

  return api;
};

/**
 * Type definitions for API responses
 */

export interface BacktestJob {
  id: string;
  job_id: string;
  symbol: string;
  exchange: string;
  strategy_type: string;
  status: string;
  progress: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  current_generation?: number;
  total_generations?: number;
  current_phase?: string;
}

export interface BacktestResult {
  id: string;
  strategy_name: string;
  symbol: string;
  total_return: number;
  sharpe_ratio?: number;
  max_drawdown: number;
  total_trades: number;
  win_rate: number;
  profit_factor: number;
}

export interface Strategy {
  id: string;
  strategy_name: string;
  strategy_type: string;
  version: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

export interface StrategyInstance {
  id: string;
  strategy_id: string;
  instance_name?: string;
  parameters: Record<string, unknown>;
  is_template: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface UserProfile {
  id: string;
  email: string;
  name?: string;
  role: string;
  tenant: {
    id: string;
    company_name: string;
    subscription_tier: string;
    max_concurrent_backtests: number;
    max_strategies: number;
  };
}
